import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import type { EmployeeDetail, EmploymentStatus } from "../../shared/types/api";

const STATUS_OPTIONS: { value: EmploymentStatus; label: string; hint: string }[] = [
  { value: "PROBATION", label: "Probation", hint: "Working and logging time." },
  { value: "ACTIVE", label: "Active", hint: "Working and logging time." },
  {
    value: "NOTICE_PERIOD",
    label: "Notice period",
    hint: "Still working. Their account stays active until they leave.",
  },
  {
    value: "SUSPENDED",
    label: "Suspended",
    hint: "Not working. Their account remains but they should not be allocated.",
  },
  {
    value: "EXITED",
    label: "Exited",
    hint: "They have left. Their account is disabled and every session ends immediately.",
  },
];

/**
 * Records a change of employment status, including departure (§9).
 *
 * <p>Exiting never deletes the record. Worklogs, memberships and historical reports all point at
 * it, so removing it would either cascade through years of history or leave it dangling. What
 * departure does is disable the account and end every live session — otherwise a leaver stays
 * signed in until their refresh token expires, which could be weeks.
 */
export function EmploymentStatusDrawer({
  employee,
  onClose,
  onSaved,
}: {
  employee: EmployeeDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<EmploymentStatus>("ACTIVE");
  const [dateOfExit, setDateOfExit] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (employee) {
      setStatus(employee.employmentStatus);
      setDateOfExit(employee.dateOfExit ?? new Date().toISOString().slice(0, 10));
      setError(null);
    }
  }, [employee]);

  const isExit = status === "EXITED";
  const selected = STATUS_OPTIONS.find((option) => option.value === status);

  const submit = async () => {
    if (!employee) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/employees/${employee.id}/employment-status`, {
        employmentStatus: status,
        dateOfExit: isExit ? dateOfExit : undefined,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not change the status.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(employee)}
      onClose={onClose}
      title="Employment status"
      subtitle={employee?.fullName}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={isExit ? "danger" : "primary"}
            onClick={() => void submit()}
            loading={saving}
          >
            {saving ? "Saving…" : isExit ? "Record departure" : "Save status"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Status" required hint={selected?.hint}>
          <Select value={status} onChange={(event) => setStatus(event.target.value as EmploymentStatus)}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        {isExit && (
          <>
            <Field
              label="Last working day"
              required
              hint="Cannot be before their joining date."
            >
              <Input
                type="date"
                value={dateOfExit}
                onChange={(event) => setDateOfExit(event.target.value)}
              />
            </Field>

            <InlineAlert tone="warning">
              Their account is disabled and every signed-in session ends straight away. The record
              itself is kept — worklogs, team membership and past reports all depend on it, and
              nothing about their history changes.
            </InlineAlert>
          </>
        )}
      </div>
    </Drawer>
  );
}
